import { Flex, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Goal as GoalModel, Segment } from '@feathr/blackbox';
import { Input } from '@feathr/components';
import EditFilters from '@feathr/extender/components/EditFilters';
import SegmentSelectSimple from '@feathr/extender/components/SegmentSelectSimple';
import { useStore } from '@feathr/extender/state';

import ConfigureFiltersButton from './ConfigureFiltersButton';

import * as styles from './GroupEntryGoal.css';

interface IProps {
  goal: GoalModel;
}

function GroupEntryGoal({ goal }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Segments } = useStore();
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | undefined>();
  const {
    spacing: { '3': selectContainerGap, '4': verticalGap },
  } = useMantineTheme();

  useEffect(() => {
    const segmentId = goal.get('segment');
    if (segmentId) {
      setSelectedSegmentId(segmentId);
    }
  }, []);

  const segment: Segment | undefined = selectedSegmentId
    ? Segments.get(selectedSegmentId)
    : undefined;

  function handleSegmentChange({ id }: { id: string }): void {
    setSelectedSegmentId(id);
    goal.set({ segment: id });
  }

  async function handleSegmentCreate(newSegment: Segment): Promise<void> {
    const goalName = t('Group created for {{- goalName }}', { goalName: goal.get('name') });
    newSegment.set({ name: goalName });
    setSelectedSegmentId(newSegment.id);
    goal.set({ segment: newSegment.id });
  }

  function handleToggleFilters(): void {
    setShowFilters((prev) => !prev);
  }

  return (
    <Flex direction={'column'} gap={verticalGap}>
      <Flex align={'flex-end'} gap={selectContainerGap}>
        <SegmentSelectSimple
          filters={{ goal_eligible: true }}
          label={t('Group')}
          name={'group-entry-segment'}
          onChange={handleSegmentChange}
          onCreate={handleSegmentCreate}
          value={selectedSegmentId}
          wrapperClassName={styles.selectWrapper}
        />
        {segment && <ConfigureFiltersButton onClick={handleToggleFilters} />}
      </Flex>
      {segment?.isEphemeral && (
        <Input attribute={'name'} label={t('Name')} model={segment} required={true} type={'text'} />
      )}

      {segment && showFilters && (
        <EditFilters filterContext={'campaignGoal'} hideGauge={true} segment={segment} />
      )}
    </Flex>
  );
}

export default observer(GroupEntryGoal);
